import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Dashboard as CorkboardIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { selectCorkboards, setCorkboards, addCorkboard, removeCorkboard } from './corkboardsSlice';
import { getCorkboardEditorUrl } from '../../urls';
import useThemeStyles from '../../hooks/useThemeStyles';
import corkboardService from '../../utils/apiRequests/corkboardService';

/**
 * Normalize backend corkboard data to frontend format
 * Backend uses _id, frontend uses id
 * Backend sends Date objects as ISO strings in JSON, convert to epoch timestamps
 */
const normalizeCorkboard = (backendCorkboard) => {
  if (!backendCorkboard) return null;

  const normalized = {
    ...backendCorkboard,
    id: backendCorkboard.id || backendCorkboard._id?.toString() || backendCorkboard._id,
    projectId: backendCorkboard.projectId?.toString() || backendCorkboard.projectId,
  };

  // Convert Date strings to epoch timestamps
  if (backendCorkboard.createdAt) {
    if (typeof backendCorkboard.createdAt === 'string') {
      normalized.createdAt = new Date(backendCorkboard.createdAt).getTime();
    } else if (backendCorkboard.createdAt instanceof Date) {
      normalized.createdAt = backendCorkboard.createdAt.getTime();
    } else {
      normalized.createdAt = backendCorkboard.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendCorkboard.updatedAt) {
    if (typeof backendCorkboard.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendCorkboard.updatedAt).getTime();
    } else if (backendCorkboard.updatedAt instanceof Date) {
      normalized.updatedAt = backendCorkboard.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendCorkboard.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

/**
 * CorkboardsListPage - Displays all corkboards for a project
 */
const CorkboardsListPage = () => {
  const { colors, isDark } = useThemeStyles();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allCorkboards = useSelector(selectCorkboards);
  const corkboards = useMemo(
    () => allCorkboards.filter((cb) => cb.projectId === projectId),
    [allCorkboards, projectId]
  );
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Load corkboards from backend on mount
  useEffect(() => {
    const loadCorkboards = async () => {
      if (!projectId) return;

      setLoading(true);
      try {
        const response = await corkboardService.getCorkboards(projectId);

        if (response.error) {
          console.error('Error loading corkboards:', response.error);
          // If error, keep existing state (might be from persistence)
          setLoading(false);
          return;
        }

        if (response.data?.corkboards) {
          const normalizedCorkboards = response.data.corkboards.map(normalizeCorkboard);
          dispatch(setCorkboards(normalizedCorkboards));
        }
      } catch (err) {
        console.error('Error loading corkboards:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCorkboards();
  }, [projectId, dispatch]);

  const handleCreateCorkboard = async () => {
    if (!projectId || creating) return;

    setCreating(true);
    try {
      const newCorkboardData = {
        name: `Corkboard ${corkboards.length + 1}`,
        notes: [],
        drawings: [],
        viewState: {
          panX: 0,
          panY: 0,
          zoom: 1,
        },
      };

      const response = await corkboardService.createCorkboard(projectId, newCorkboardData);

      if (response.error) {
        console.error('Error creating corkboard:', response.error);
        return;
      }

      if (response.data?.data) {
        const normalizedCorkboard = normalizeCorkboard(response.data.data);
        dispatch(addCorkboard(normalizedCorkboard));
        // Navigate to the new corkboard
        navigate(getCorkboardEditorUrl(projectId, normalizedCorkboard.id));
      }
    } catch (err) {
      console.error('Error creating corkboard:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCorkboard = (corkboardId) => {
    navigate(getCorkboardEditorUrl(projectId, corkboardId));
  };

  const handleDeleteCorkboard = async (e, corkboardId) => {
    e.stopPropagation();

    if (!projectId) return;

    try {
      const response = await corkboardService.deleteCorkboard(projectId, corkboardId);

      if (response.error) {
        console.error('Error deleting corkboard:', response.error);
        return;
      }

      // Remove from Redux store on success
      dispatch(removeCorkboard(corkboardId));
    } catch (err) {
      console.error('Error deleting corkboard:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.primary,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ color: colors.text.primary }}>
          Corkboards
        </Typography>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          onClick={handleCreateCorkboard}
          disabled={creating}
        >
          Create New Corkboard
        </Button>
      </Box>

      {corkboards.length > 0 ? (
        <Grid container spacing={2}>
          {corkboards.map((corkboard) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={corkboard.id}>
              <Card
                variant="outlined"
                sx={{
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  backgroundColor: colors.background.secondary,
                  borderColor: colors.border.subtle,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : 3,
                  },
                }}
              >
                <CardActionArea onClick={() => handleOpenCorkboard(corkboard.id)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CorkboardIcon sx={{ fontSize: 28, mt: 0.25, color: colors.icon.primary }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          noWrap
                          sx={{ fontWeight: 'bold', color: colors.text.primary }}
                        >
                          {corkboard.name || 'Untitled Corkboard'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                          {corkboard.notes?.length || 0} notes • {corkboard.drawings?.length || 0}{' '}
                          drawings
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: colors.text.secondary }}
                        >
                          Created: {formatDate(corkboard.createdAt)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteCorkboard(e, corkboard.id)}
                        sx={{
                          opacity: 0.5,
                          color: colors.text.secondary,
                          '&:hover': { opacity: 1, color: 'error.main' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: colors.text.secondary,
            gap: 2,
          }}
        >
          <CorkboardIcon sx={{ fontSize: 64, opacity: 0.3, color: colors.text.muted }} />
          <Typography variant="h6" sx={{ color: colors.text.primary }}>
            No corkboards yet
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Create a corkboard to organize your notes visually on an infinite canvas.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateCorkboard}
            sx={{ mt: 2 }}
          >
            Create Your First Corkboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CorkboardsListPage;
